import {
  BMCheckbox,
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcSystemCheckmark16 from "@traveloka/icon-kit-web/react/IcSystemCheckmark16";
import { Image, StyleSheet } from "react-native";
import { INSURANCE_ITEM_ILLUSTRATION_BASE64 } from "../../../../assets/illustration-add-ons";
import BMView from "../../../../components/View/BMView";
import { AddOnCheckBoxDisplayInfo } from "./types";

const INSURANCE_ICON_SIZE = 32;

type Props = {
  item: AddOnCheckBoxDisplayInfo;
  onCheckOption?: () => void;
  handleMoreInfoPress?: (link: string) => void;
};

export default function FlightPrebookCrossSellAddOnsItem({
  item,
  onCheckOption,
  handleMoreInfoPress,
}: Props) {
  const { BMComponents } = useBMTheme();
  const styles = createStyles(BMComponents);

  const {
    moreInfo,
    isChecked,
    cardTitleText,
    cardDescriptionText,
    cardDescriptionIconUrl,
    primaryCardAdditionalInfo,
    secondaryCardAdditionalInfo,
    priceDisplayV2Info: { selectedPriceDisplayInfo: priceInfoList },
  } = item;

  const additionalInfos = [
    ...(primaryCardAdditionalInfo || []),
    ...(secondaryCardAdditionalInfo || []),
  ];

  const moreInfoLink = moreInfo.context?.link;

  return (
    <BMView style={styles.card} spacing="sm">
      <BMView row justify="between" spacing="md">
        <BMView row spacing="lg">
          <Image
            source={{
              uri: cardDescriptionIconUrl || INSURANCE_ITEM_ILLUSTRATION_BASE64,
            }}
            style={{ width: INSURANCE_ICON_SIZE, height: INSURANCE_ICON_SIZE }}
          />
          <BMView spacing="xs">
            <BMText variant="uiLarge">{cardTitleText}</BMText>
            <BMView>
              {priceInfoList.map((info, index) => (
                <InsurancePriceInfo key={index} {...info} />
              ))}
            </BMView>
          </BMView>
        </BMView>
        <BMView>
          <BMCheckbox value={isChecked} onChange={onCheckOption} />
        </BMView>
      </BMView>
      <BMView spacing="md">
        <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
          {cardDescriptionText}
        </BMText>
        <BMView spacing="sm">
          {additionalInfos.map((info, index) => (
            <BMView
              row
              spacing="sm"
              align="center"
              key={`additional-infos-${index}`}
            >
              <BMView>
                <IcSystemCheckmark16
                  width={16}
                  height={16}
                  color={BMComponents.BMStyleIconInkHighlightGreen}
                />
              </BMView>
              <BMText variant="uiNormalLi">{info.title}</BMText>
            </BMView>
          ))}
          {moreInfoLink && moreInfo.text ? (
            <BMText
              variant="uiNormal"
              ink="BMStyleTextInkAction"
              onPress={() => handleMoreInfoPress?.(moreInfoLink)}
            >
              {moreInfo.text}
            </BMText>
          ) : null}
        </BMView>
      </BMView>
    </BMView>
  );
}

type InsurancePriceInfoProps = {
  text: string;
  type: string;
};

function InsurancePriceInfo({ text, type }: InsurancePriceInfoProps) {
  if (!text) return null;
  if (type === "STRIKETHROUGH_PRICE") {
    return (
      <BMText
        style={{ textDecorationLine: "line-through" }}
        variant="uiSmallLi"
        ink="BMStyleTextInkSecondary"
      >
        {text}
      </BMText>
    );
  }
  if (type === "TOTAL_PRICE") {
    return (
      <BMText variant="uiNormal" ink="BMStyleTextInkPrice">
        {text}
      </BMText>
    );
  }
  return null;
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    card: {
      padding: BMPrimitives.BMSize.md,
      paddingRight: BMPrimitives.BMSize.xs / 2,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMPrimitives.BMBorder.radius.default,
    },
  });
}
